/**
 * Schritt 1: Individuelle-Parameter-Eingabe
 * Ermöglicht die Konfiguration von Steuerjahr, Familienstand und Kirchensteuer
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Slider,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';

import { SteuerParameter } from '@/types';

interface Schritt1Props {
  steuerParameter: SteuerParameter | null;
  onParameterUpdate: (parameter: SteuerParameter) => void | Promise<void>;
}

/**
 * Parst einen Eingabestring zu einer Zahl und fällt nur bei ungültigen
 * Eingaben (leer, NaN) auf den Fallback zurück – legitime 0-Eingaben bleiben erhalten.
 */
const parseWithDefault = (value: string, fallback: number): number => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Begrenzt die Monte-Carlo-Iterationen auf den gültigen Bereich [100, 50000] */
const clampMonteCarloIterations = (value: number): number =>
  Math.min(50000, Math.max(100, value));

/**
 * Komponente für die Eingabe der Berechnungsparameter
 */
const Schritt1: React.FC<Schritt1Props> = ({ steuerParameter, onParameterUpdate }) => {
  // Lokaler State für die Formular-Eingaben mit Standardwerten
  const [anzahlJahreTopf2, setAnzahlJahreTopf2] = useState<string>('3');
  const [retirementAge, setRetirementAge] = useState<string>('65');
  const [berechnungBisLebensalter, setBerechnungBisLebensalter] = useState<string>('95');
  const [zinssatzVerbrauchstopf, setZinssatzVerbrauchstopf] = useState<string>('1.5');
  const [zinssatzZinstopf, setZinssatzZinstopf] = useState<string>('3.0');
  const [renditeInvestitionstopf, setRenditeInvestitionstopf] = useState<string>('7.0');
  const [inflationsrate, setInflationsrate] = useState<string>('2.0');

  const [vorhandenesKapital, setVorhandenesKapital] = useState<string>('1000000');
  const [portfolioEntnahmeRate, setPortfolioEntnahmeRate] = useState<number>(3.0);
  const [monteCarloIterations, setMonteCarloIterations] = useState<string>('10000');

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Ref to store debounce timeout
  const sliderDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Initialisierung mit vorhandenen Parametern oder Standardwerten
  useEffect(() => {
    if (steuerParameter) {
      setAnzahlJahreTopf2(steuerParameter.anzahlJahreTopf2?.toString() || '3');
      setRetirementAge(steuerParameter.retirementAge?.toString() || '65');
      setBerechnungBisLebensalter(steuerParameter.berechnungBisLebensalter?.toString() || '95');
      setZinssatzVerbrauchstopf(steuerParameter.zinssatzVerbrauchstopf?.toString() || '1.5');
      setZinssatzZinstopf(steuerParameter.zinssatzZinstopf?.toString() || '3.0');
      setRenditeInvestitionstopf(steuerParameter.renditeInvestitionstopf?.toString() || '7.0');
      setInflationsrate(steuerParameter.inflationsrate?.toString() || '2.0');

      setVorhandenesKapital(steuerParameter.vorhandenesKapital?.toString() || '1000000');
      setMonteCarloIterations(steuerParameter.monteCarloIterations?.toString() || '10000');
      // Ensure portfolio rate is always a multiple of 0.1 (slider step)
      const rate = steuerParameter.portfolioEntnahmeRate ?? 3.0;
      const roundedRate = Math.round(rate * 10) / 10;
      setPortfolioEntnahmeRate(roundedRate);
    } else {
      // Automatisches Speichern der Standardwerte beim ersten Laden
      const standardParameter: SteuerParameter = {
        anzahlJahreTopf2: 3,
        retirementAge: 65,
        berechnungBisLebensalter: 95,
        zinssatzVerbrauchstopf: 1.5,
        zinssatzZinstopf: 3.0,
        renditeInvestitionstopf: 7.0,
        inflationsrate: 2.0,

        vorhandenesKapital: 1000000,
        portfolioEntnahmeRate: 3.0,
        monteCarloIterations: 10000,
      };
      onParameterUpdate(standardParameter);
    }
  }, [steuerParameter, onParameterUpdate]);


  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (sliderDebounceRef.current) {
        clearTimeout(sliderDebounceRef.current);
      }
    };
  }, []);

  // Blur handler for auto-save
  const handleFieldBlur = useCallback(async () => {
    setSaveStatus('saving');

    try {
      const parameter: SteuerParameter = {
        anzahlJahreTopf2: parseWithDefault(anzahlJahreTopf2, 3),
        retirementAge: parseWithDefault(retirementAge, 65),
        berechnungBisLebensalter: parseWithDefault(berechnungBisLebensalter, 95),
        zinssatzVerbrauchstopf: parseWithDefault(zinssatzVerbrauchstopf, 1.5),
        zinssatzZinstopf: parseWithDefault(zinssatzZinstopf, 3.0),
        renditeInvestitionstopf: parseWithDefault(renditeInvestitionstopf, 7.0),
        inflationsrate: parseWithDefault(inflationsrate, 2.0),
        vorhandenesKapital: parseWithDefault(vorhandenesKapital, 1000000),
        portfolioEntnahmeRate: Number.isFinite(portfolioEntnahmeRate)
          ? portfolioEntnahmeRate
          : 3.0,
        monteCarloIterations: clampMonteCarloIterations(
          parseWithDefault(monteCarloIterations, 10000)
        ),
      };

      await onParameterUpdate(parameter);
      setSaveStatus('saved');

      // Reset to idle after showing "saved" status
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus('idle');
    }
  }, [
    anzahlJahreTopf2,
    retirementAge,
    berechnungBisLebensalter,
    zinssatzVerbrauchstopf,
    zinssatzZinstopf,
    renditeInvestitionstopf,
    inflationsrate,
    vorhandenesKapital,
    portfolioEntnahmeRate,
    monteCarloIterations,
    onParameterUpdate,
  ]);

  // Debounced slider save handler
  const handleSliderChange = useCallback(
    async (value: number) => {

      // Clear previous timeout if it exists
      if (sliderDebounceRef.current) {
        clearTimeout(sliderDebounceRef.current);
      }

      // Set new timeout
      sliderDebounceRef.current = setTimeout(async () => {
        try {
          setSaveStatus('saving');

          const parameter: SteuerParameter = {
            anzahlJahreTopf2: parseWithDefault(anzahlJahreTopf2, 3),
            retirementAge: parseWithDefault(retirementAge, 65),
            berechnungBisLebensalter: parseWithDefault(berechnungBisLebensalter, 95),
            zinssatzVerbrauchstopf: parseWithDefault(zinssatzVerbrauchstopf, 1.5),
            zinssatzZinstopf: parseWithDefault(zinssatzZinstopf, 3.0),
            renditeInvestitionstopf: parseWithDefault(renditeInvestitionstopf, 7.0),
            inflationsrate: parseWithDefault(inflationsrate, 2.0),
            vorhandenesKapital: parseWithDefault(vorhandenesKapital, 1000000),
            portfolioEntnahmeRate: value,
            monteCarloIterations: clampMonteCarloIterations(
              parseWithDefault(monteCarloIterations, 10000)
            ),
          };

          await onParameterUpdate(parameter);
          setSaveStatus('saved');

          // Reset to idle after showing "saved" status
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
          console.error('Slider auto-save failed:', error);
          setSaveStatus('idle');
        }
      }, 1000); // 1 second debounce for slider
    },
    [
      anzahlJahreTopf2,
      retirementAge,
      berechnungBisLebensalter,
      zinssatzVerbrauchstopf,
      zinssatzZinstopf,
      renditeInvestitionstopf,
      inflationsrate,
      vorhandenesKapital,
      monteCarloIterations,
      onParameterUpdate,
    ]
  );


  // Note: Tax-related dropdown options removed

  // Topf-Allokation für die KPI-Anzeige – identisch zur Engine-Regel, damit die
  // Summe der angezeigten Töpfe das vorhandene Kapital nie überschreiten kann
  const kapitalKPI = parseFloat(vorhandenesKapital) || 0;
  const portfolioEntnahmeKPI = (kapitalKPI * portfolioEntnahmeRate) / 100;
  const verbrauchstopfKPI = Math.min(portfolioEntnahmeKPI, kapitalKPI);
  const zinstopfZielKPI = (parseFloat(anzahlJahreTopf2) || 0) * portfolioEntnahmeKPI;
  const zinstopfKPI = Math.min(zinstopfZielKPI, kapitalKPI - verbrauchstopfKPI);
  const investitionstopfKPI = kapitalKPI - verbrauchstopfKPI - zinstopfKPI;

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Geben Sie die allgemeinen Parameter für diese Simulation ein."
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {saveStatus !== 'idle' && (
                <StatusIndicator
                  type={
                    saveStatus === 'saving'
                      ? 'in-progress'
                      : saveStatus === 'saved'
                        ? 'success'
                        : 'info'
                  }
                >
                  {saveStatus === 'saving'
                    ? 'Speichere...'
                    : saveStatus === 'saved'
                      ? 'Gespeichert'
                      : ''}
                </StatusIndicator>
              )}
            </SpaceBetween>
          }
        >
          Berechnungsparameter
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <SpaceBetween direction="vertical" size="l">
          <ColumnLayout columns={2} borders="vertical">
            {/* Left Side: All Parameter Inputs */}
            <SpaceBetween direction="vertical" size="m">
              {/* Note: Tax-related fields removed */}

              {/* 3-Topf Strategy Parameters */}
              <Box variant="div">
                <Box variant="h4">3-Topf Strategie Parameter</Box>

                {/* Portfolio-Entnahme Kontrollen */}
                <Box variant="div" margin={{ top: 'm', bottom: 'm' }}>
                  <Box variant="h5">Portfolio-Entnahme</Box>
                  <ColumnLayout columns={2} borders="horizontal">
                    <Box>
                      <FormField
                        label="Vorhandenes Kapital (€)"
                        description="Das verfügbare Gesamtkapital für Portfolio-Entnahmen"
                      >
                        <Input
                          value={vorhandenesKapital}
                          onChange={({ detail }) => {
                            const value = detail.value;
                            // Allow empty string and valid numbers (including 0)
                            if (
                              value === '' ||
                              (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)
                            ) {
                              setVorhandenesKapital(value);
                            }
                          }}
                          onBlur={handleFieldBlur}
                          type="number"
                          placeholder="1000000"
                          inputMode="decimal"
                        />
                      </FormField>
                    </Box>
                    <Box>
                      <FormField
                        label={`Portfolio-Entnahme: ${portfolioEntnahmeRate.toFixed(1)}% (${(() => {
                          const kapital = parseFloat(vorhandenesKapital) || 0;
                          return ((kapital * portfolioEntnahmeRate) / 100).toLocaleString('de-DE');
                        })()} €)`}
                        description="Entnahmepercentage vom vorhandenen Kapital per Slider"
                      >
                        <Slider
                          value={Math.round(portfolioEntnahmeRate * 10) / 10}
                          onChange={({ detail }) => {
                            // Ensure value is properly rounded to step (0.1)
                            const roundedValue = Math.round(detail.value * 10) / 10;
                            setPortfolioEntnahmeRate(roundedValue);
                            // Trigger debounced save
                            handleSliderChange(roundedValue);
                          }}
                          min={0}
                          max={10}
                          step={0.1}
                          hideFillLine={false}
                        />
                      </FormField>
                    </Box>
                  </ColumnLayout>
                </Box>

                <ColumnLayout columns={2}>
                  <Box>
                    <FormField
                      label="Anzahl Jahre Topf 2"
                      description="Anzahl von Jahren im Zinstopf"
                    >
                      <Input
                        value={anzahlJahreTopf2}
                        onChange={({ detail }) => setAnzahlJahreTopf2(detail.value)}
                        onBlur={handleFieldBlur}
                        type="number"
                        placeholder="3"
                        inputMode="numeric"
                      />
                    </FormField>
                  </Box>
                  <Box>
                    <FormField
                      label="Anzahl Monte-Carlo Iterationen"
                      description="Anzahl der Iterationen für Monte-Carlo-Simulationen (empfohlen: 1000-10000)"
                    >
                      <Input
                        value={monteCarloIterations}
                        onChange={({ detail }) => {
                          const value = detail.value;
                          // Allow empty string and any numeric intermediate state while typing;
                          // clamping to [100, 50000] happens onBlur / on save
                          if (value === '' || !isNaN(parseFloat(value))) {
                            setMonteCarloIterations(value);
                          }
                        }}
                        onBlur={() => {
                          // Clamp to the valid range [100, 50000] when leaving the field
                          const parsed = parseFloat(monteCarloIterations);
                          if (Number.isFinite(parsed)) {
                            setMonteCarloIterations(
                              clampMonteCarloIterations(parsed).toString()
                            );
                          } else {
                            setMonteCarloIterations('10000');
                          }
                          handleFieldBlur();
                        }}
                        type="number"
                        placeholder="10000"
                        inputMode="numeric"
                      />
                    </FormField>
                  </Box>
                  <Box>
                    <FormField
                      label="Alter bei Rentenbeginn"
                      description="Ihr Alter bei Rentenbeginn"
                    >
                      <Input
                        value={retirementAge}
                        onChange={({ detail }) => setRetirementAge(detail.value)}
                        onBlur={handleFieldBlur}
                        type="number"
                        placeholder="65"
                        inputMode="numeric"
                      />
                    </FormField>
                  </Box>

                  <Box>
                    <FormField
                      label="Berechnung bis Lebensalter"
                      description="Bis zu welchem Lebensalter soll berechnet werden"
                    >
                      <Input
                        value={berechnungBisLebensalter}
                        onChange={({ detail }) => setBerechnungBisLebensalter(detail.value)}
                        onBlur={handleFieldBlur}
                        type="number"
                        placeholder="85"
                        inputMode="numeric"
                      />
                    </FormField>
                  </Box>
                </ColumnLayout>
              </Box>

              {/* Zinssätze und Renditen */}
              <Box variant="div">
                <Box variant="h4">Zinssätze und Renditen</Box>
                <ColumnLayout columns={2}>
                  <Box>
                    <FormField
                      label="Zinssatz Verbrauchstopf (%)"
                      description="Jährlicher Zinssatz für den Verbrauchstopf"
                    >
                      <Input
                        value={zinssatzVerbrauchstopf}
                        onChange={({ detail }) => setZinssatzVerbrauchstopf(detail.value)}
                        onBlur={handleFieldBlur}
                        type="number"
                        placeholder="1.5"
                        inputMode="decimal"
                      />
                    </FormField>
                  </Box>
                  <Box>
                    <FormField
                      label="Zinssatz Zinstopf (%)"
                      description="Jährlicher Zinssatz für den Zinstopf"
                    >
                      <Input
                        value={zinssatzZinstopf}
                        onChange={({ detail }) => setZinssatzZinstopf(detail.value)}
                        onBlur={handleFieldBlur}
                        type="number"
                        placeholder="3.0"
                        inputMode="decimal"
                      />
                    </FormField>
                  </Box>
                  <Box>
                    <FormField
                      label="Rendite Investitionstopf (%)"
                      description="Erwartete jährliche Rendite für den Investitionstopf"
                    >
                      <Input
                        value={renditeInvestitionstopf}
                        onChange={({ detail }) => setRenditeInvestitionstopf(detail.value)}
                        onBlur={handleFieldBlur}
                        type="number"
                        placeholder="7.0"
                        inputMode="decimal"
                      />
                    </FormField>
                  </Box>
                  <Box>
                    <FormField label="Inflationsrate (%)" description="Jährliche Inflationsrate">
                      <Input
                        value={inflationsrate}
                        onChange={({ detail }) => setInflationsrate(detail.value)}
                        onBlur={handleFieldBlur}
                        type="number"
                        placeholder="2.0"
                        inputMode="decimal"
                      />
                    </FormField>
                  </Box>
                </ColumnLayout>
              </Box>
            </SpaceBetween>

            {/* Right Side: Live KPIs */}
            <SpaceBetween direction="vertical" size="m">
              {/* Tax-related displays removed */}

              {/* KPIs Portfolio-Entnahme - Compact Grid Layout */}
              {vorhandenesKapital !== '' && portfolioEntnahmeRate >= 0 && (
                <Box variant="div">
                  <Box variant="h4" margin={{ bottom: 'xs' }}>
                    Portfolio-Entnahme KPIs
                  </Box>
                  <Box variant="small" color="text-body-secondary" margin={{ bottom: 'm' }}>
                    Zeigt die Startallokation der drei Töpfe in Jahr 0. Der Verbrauchstopf ist
                    danach kein dauerhaft gehaltener Puffer, sondern wird jedes Jahr vor der
                    Entnahme aufgefüllt und im selben Jahr wieder geleert.
                  </Box>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '12px',
                    }}
                  >
                    {/* Verbrauchstopf - Portfolio-Entnahme */}
                    <div
                      style={{
                        padding: '16px',
                        border: '1px solid #d5dbdb',
                        borderRadius: '8px',
                        backgroundColor: '#fafbfc',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <Box variant="awsui-key-label">Verbrauchstopf</Box>
                        <Box variant="awsui-value-large" color="text-status-success">
                          {verbrauchstopfKPI.toLocaleString('de-DE')}{' '}
                          EUR
                        </Box>
                      </div>
                      <div
                        style={{
                          marginTop: '8px',
                          borderTop: '1px solid #e9ecef',
                          paddingTop: '8px',
                        }}
                      >
                        <Box variant="small" color="text-body-secondary">
                          Portfolio-Entnahme: {portfolioEntnahmeRate.toFixed(1)}%
                        </Box>
                      </div>
                    </div>

                    {/* Zinstopf - Anzahl Jahre Topf 2 x Portfolio-Entnahme */}
                    {anzahlJahreTopf2 && (
                      <div
                        style={{
                          padding: '16px',
                          border: '1px solid #d5dbdb',
                          borderRadius: '8px',
                          backgroundColor: '#fafbfc',
                          minHeight: '120px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <Box variant="awsui-key-label">Zinstopf</Box>
                          <Box variant="awsui-value-large" color="text-status-success">
                            {zinstopfKPI.toLocaleString('de-DE')}{' '}
                            EUR
                          </Box>
                        </div>
                        <div
                          style={{
                            marginTop: '8px',
                            borderTop: '1px solid #e9ecef',
                            paddingTop: '8px',
                          }}
                        >
                          <Box variant="small" color="text-body-secondary">
                            {anzahlJahreTopf2} Jahre x Portfolio-Entnahme:{' '}
                            {portfolioEntnahmeRate.toFixed(1)}%
                          </Box>
                        </div>
                      </div>
                    )}

                    {/* Investitionstopf - Needs retirementAge, berechnungBisLebensalter, and anzahlJahreTopf2 */}
                    {retirementAge && berechnungBisLebensalter && anzahlJahreTopf2 && (
                      <div
                        style={{
                          padding: '16px',
                          border: '1px solid #d5dbdb',
                          borderRadius: '8px',
                          backgroundColor: '#fafbfc',
                          minHeight: '120px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <Box variant="awsui-key-label">Investitionstopf</Box>
                          <Box variant="awsui-value-large" color="text-status-success">
                            {investitionstopfKPI.toLocaleString('de-DE')}{' '}
                            EUR
                          </Box>
                        </div>
                        <div
                          style={{
                            marginTop: '8px',
                            borderTop: '1px solid #e9ecef',
                            paddingTop: '8px',
                          }}
                        >
                          <Box variant="small" color="text-body-secondary">
                            Vorhandenes Kapital minus Verbrauchstopf- und Zinstopf-Allokation
                          </Box>
                        </div>
                      </div>
                    )}
                  </div>
                </Box>
              )}
            </SpaceBetween>
          </ColumnLayout>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};

export default Schritt1;
